import React, {FC, useEffect, useState} from "react";
import {CardLoading} from "../../../common/components/card-loading";
import {AccountCard} from "./common";
import {Separation} from "../../../common/components/separation";
import {AppointmentCard} from "./appointment-card";
import {Appointment} from "../account.types";
import {Pagination} from "../../../common/commons.types";
import {
    IconName,
    useToaster,
    InOut,
    Svg,
    BaseButton,
    SpanLink,
    SpanCaption2
} from '@linkeo.com/ui-lib-react';
import {useConfirm} from "../../../common/providers/confirm-provider";
import {useIntl, FormattedMessage} from "react-intl";
import {useTheme} from "styled-components";

interface AppointmentStatusListProps {
    title: string;
    fetchAppointmentsList: (offset: number) => Promise<Pagination<Appointment>>
    timezone: string | null
    isCancelable?: (date: string) => boolean;
    icon: IconName
    showTotal: boolean
    cancelAppointment: (id: string) => Promise<void>
    resetAppointments: () => void
}

export const AppointmentStatusList: FC<AppointmentStatusListProps> = ({
                                                                          title,
                                                                          fetchAppointmentsList,
                                                                          timezone,
                                                                          isCancelable,
                                                                          icon,
                                                                          showTotal,
                                                                          cancelAppointment,
                                                                          resetAppointments
                                                                      }) => {
    const toast = useToaster();
    const {openConfirm} = useConfirm();
    const intl = useIntl();
    const theme = useTheme();
    const [loading, setLoading] = useState<boolean>(false);
    const [fetching, setFetching] = useState<boolean>(true);
    const [appointments, setAppointments] = useState<Appointment[]>([]);
    const [total, setTotal] = useState<number>(0);
    const [lock, setLock] = useState<boolean>(true);
    const [error, setError] = useState<boolean>(false);
    const [loader, setLoader] = useState<boolean>(false);

    useEffect(() => {
        fetchAppointmentsList(0).then(ap => {
            setAppointments(ap.result);
            setTotal(ap.total)
        }).catch((e) => {
            console.error(e);
            setError(true)
        }).finally(() => setFetching(false))
    }, [fetchAppointmentsList]);

    useEffect(() => {
        if (appointments.length < total) {
            setLock(false)
        } else {
            setLock(true)
        }
    }, [appointments, total])

    const clickCancelAppointment = async (appointmentId: string) => {
        await openConfirm({
            content: intl.formatMessage({
                id: 'AccountPageCancelAppointmentConfirmation',
                defaultMessage: "Êtes-vous sûr(e) de vouloir annuler ce rendez-vous ?"
            }),
            title: intl.formatMessage({id: 'AccountPageModalCancelConfirmationTitle', defaultMessage: "Annuler le RDV"})
        }).then(async () => {
            setLoading(true);
            try {
                await cancelAppointment(appointmentId)
                const resp = await fetchAppointmentsList(0);
                setAppointments(resp.result)
                setTotal(resp.total);
                resetAppointments();
            } catch (e) {
                console.error(e)
                setError(true)
            } finally {
                setLoading(false)
            }
        });
    }

    const onLoadMoreClick = async () => {
        setLoader(true);
        try {
            const list = await fetchAppointmentsList(appointments.length);
            setAppointments([...appointments, ...list.result])
        } catch (e) {
            console.error(e);
            toast(intl.formatMessage({id: 'toastMessageError', defaultMessage: 'Une erreur est survenue'}))
        } finally {
            setLoader(false)
        }
    }

    return loading || fetching ? <CardLoading/> :
        <>
            <AccountCard icon={icon}
                         titleName={(!error && showTotal ? total.toString() + " " : '') + title}>
                {error
                    ? <div style={{marginTop: '12px'}}>
                        <SpanCaption2 style={{color: theme.colors.danger}}>
                            ...<FormattedMessage
                            id={"appointmentsStatusListErrorMessage"}
                            defaultMessage={"Impossible d'afficher les rendez-vous"}/>...
                        </SpanCaption2>
                    </div>
                    : appointments.map(e => {
                        return <React.Fragment key={e.id}>
                            <Separation/>
                            <AppointmentCard
                                titlePrestation={e.service.title}
                                datePrestation={e.date}
                                timezone={timezone}
                                clickCancelAppointment={isCancelable && isCancelable(e.date) ? () => clickCancelAppointment(e.id) : undefined}/>
                        </React.Fragment>
                    })}
                {!lock && !loader && <div style={{textAlign: 'center', marginTop: '24px'}}>
                    <BaseButton onClick={onLoadMoreClick}>
                        <SpanLink>
                            + <FormattedMessage id={'accountpageLoadMoreItems'} defaultMessage={'Charger plus'}/>
                        </SpanLink>
                    </BaseButton>
                </div>}
                <InOut show={loader} keepContent={true}>
                    <div style={{textAlign: 'center', marginTop: '24px'}}>
                        <Svg animatedIcon={'spinner'} stroke={theme.colors.grey40} height={'24px'}/>
                    </div>
                </InOut>
            </AccountCard>
        </>
}